import "./SNStatus.scss";
import React from "react";
import { useEffect, useRef, useState, useContext } from "react";
import { useAppSelector } from "../../../../../redux/hooks";
import { useDispatch } from "react-redux";
import Plot from "react-plotly.js";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { CoordinateConverter } from "../../../../../util/coordinateConverter";
import { UserConfigCameraSettings } from "../../../../../util/model";

interface Props {
  width: number;
  config: UserConfigCameraSettings;
  onCurrentCameraChange: (val: string) => void;
}

export default function SNStatus(props: Props) {
  const converter = new CoordinateConverter();
  const colorGray = "rgba(140,140,140,1)";
  const colorWhite = "rgba(255,255,255,1)";
  const colorYellow = "rgba(255,255,140,1)";
  const colorRed = "rgba(255,140,140,1)";
  let ColorScale_Normal: Array<any> = [
    ["0.0", "rgb(49,54,149)"],
    ["0.05", "rgb(69,117,180)"],
    ["0.1", "rgb(116,173,209)"],
    ["0.15", "rgb(171,217,233)"],
    ["0.2", "rgb(224,243,248)"],
    ["0.3", "rgb(254,224,144)"],
    ["0.4", "rgb(253,174,97)"],
    ["0.6", "rgb(244,109,67)"],
    ["0.8", "rgb(215,48,39)"],
    ["1.0", "rgb(165,0,38)"],
  ];
  const [snIndex, setSNIndex] = useState(-1);
  const [snUBloxFix, setUBloxFix] = useState("NOFIX");
  const [snAvailL1, setAvailL1] = useState(false);
  const [snCalL1, setCalL1] = useState(false);
  const [snJammerL1, setJammerL1] = useState(false);
  const [snSpooferL1, setSpooferL1] = useState(false);
  const [snAvailL2, setAvailL2] = useState(false);
  const [snCalL2, setCalL2] = useState(false);
  const [snJammerL2, setJammerL2] = useState(false);
  const [snSpooferL2, setSpooferL2] = useState(false);
  let data_bfl1_init: Array<any> = [];
  data_bfl1_init.push({
    x: [],
    y: [],
    mode: "lines",
    name: "",
    line: { color: "rgb(255, 255, 255)" },
  });
  let layout_bfl1_init = {
    title: {
      text: "Sky Plot",
      font: {
        color: "black",
        size: 10,
      },
    },
    autosize: true,
    margin: {
      l: 2,
      r: 2,
      b: 2,
      t: 30,
      pad: 0,
    },
    showlegend: false,
    legend: {
      font: { color: "black" },
      xanchor: "right" as any,
      yanchor: "top" as any,
    },
    xaxis: {
      title: {
        text: "",
        font: {
          color: "black",
        },
      },
      dtick: 30,
      autorange: false,
      range: [-100, 100],
      tickcolor: "white",
      tickfont: {
        color: "black",
      },
      gridcolor: "white",
      mirror: true,
      linewidth: 0,
      linecolor: "white",
      scaleanchor: "y" as any,
      showticklabels: false,
    },
    yaxis: {
      title: {
        text: "",
        font: {
          color: "black",
        },
      },
      dtick: 30,
      autorange: false,
      range: [-100, 100],
      tickcolor: "white",
      tickfont: {
        color: "black",
      },
      gridcolor: "white",
      mirror: true,
      linewidth: 0,
      linecolor: "white",
      showticklabels: false,
    },
    plot_bgcolor: "white",
    paper_bgcolor: "white",
  };
  let data_bfl2_init: Array<any> = [];
  data_bfl2_init.push({
    x: [],
    y: [],
    mode: "lines",
    name: "",
    line: { color: "rgb(255, 255, 255)" },
  });
  let layout_bfl2_init = {
    title: {
      text: "Sky Plot",
      font: {
        color: "black",
        size: 10,
      },
    },
    autosize: true,
    margin: {
      l: 2,
      r: 2,
      b: 2,
      t: 30,
      pad: 0,
    },
    showlegend: false,
    legend: {
      font: { color: "black" },
      xanchor: "right" as any,
      yanchor: "top" as any,
    },
    xaxis: {
      title: {
        text: "",
        font: {
          color: "black",
        },
      },
      dtick: 30,
      autorange: false,
      range: [-100, 100],
      tickcolor: "white",
      tickfont: {
        color: "black",
      },
      gridcolor: "white",
      mirror: true,
      linewidth: 0,
      linecolor: "white",
      scaleanchor: "y" as any,
      showticklabels: false,
    },
    yaxis: {
      title: {
        text: "",
        font: {
          color: "black",
        },
      },
      dtick: 30,
      autorange: false,
      range: [-100, 100],
      tickcolor: "white",
      tickfont: {
        color: "black",
      },
      gridcolor: "white",
      mirror: true,
      linewidth: 0,
      linecolor: "white",
      showticklabels: false,
    },
    plot_bgcolor: "white",
    paper_bgcolor: "white",
  };
  const [data_bfl1, setBFL1Data] = useState(data_bfl1_init);
  const [layout_bfl1, setBFL1Layout] = useState(layout_bfl1_init);
  const [data_bfl2, setBFL2Data] = useState(data_bfl2_init);
  const [layout_bfl2, setBFL2Layout] = useState(layout_bfl2_init);
  let psd_init: Array<any> = [];
  psd_init.push({
    x: [-15.36, 15.36],
    y: [-200, -200],
    mode: "lines",
    name: "L1",
    line: { color: "rgb(0, 155, 155)" },
  });
  psd_init.push({
    x: [-15.36, 15.36],
    y: [-200, -200],
    mode: "lines",
    name: "L2",
    line: { color: "rgb(255, 155, 0)" },
  });
  let layout_psd_init = {
    height: 200,
    title: {
      text: "PSD",
      font: {
        color: "black",
        size: 12,
      },
    },
    margin: {
      l: 50,
      r: 30,
      b: 40,
      t: 30,
      pad: 0,
    },
    showlegend: true,
    legend: {
      font: { color: "black" },
    },
    xaxis: {
      title: {
        text: "Frequency (MHz)",
        font: {
          color: "black",
        },
      },
      dtick: 2,
      autorange: true,
      tickcolor: "black",
      tickfont: {
        color: "black",
      },
      gridcolor: "rgb(110, 110, 50)",
      mirror: true,
      linewidth: 1,
      linecolor: "black",
    },
    yaxis: {
      title: {
        text: "Power (dB)",
        font: {
          color: "black",
        },
      },
      range: [-120, -50],
      dtick: 10,
      autorange: true,
      tickcolor: "black",
      tickfont: {
        color: "black",
      },
      gridcolor: "rgb(110, 110, 50)",
      mirror: true,
      linewidth: 1,
      linecolor: "black",
    },
    plot_bgcolor: "white",
    paper_bgcolor: "white",
  };
  const [data_psd, setPSDData] = useState(psd_init);
  const [layout_psd, setPSDLayout] = useState(layout_psd_init);

  function onSNChange(val: any) {
    if (val.target.value >= 0 && val.target.value < SNList.length) {
      props.config.CurrentCamera = SNList[val.target.value];
      props.onCurrentCameraChange(SNList[val.target.value]);
      setSNIndex(val.target.value);
    } else {
      props.config.CurrentCamera = SNList[snIndex];
      props.onCurrentCameraChange(SNList[snIndex]);
    }
  }

  const dispatch = useDispatch();
  const layerData = useAppSelector((state) => state.layerData);
  const SNTime = layerData.status.Time;
  const SNList = layerData.status.SensorNodes;
  const SNStatus = layerData.status.SensorNodeStatus;

  useEffect(() => {
    if (SNList.length > 0) {
      setSNIndex(0);
    } else {
      setSNIndex(-1);
    }
  }, [SNList.length]);

  useEffect(() => {
    if (snIndex >= 0 && snIndex < SNStatus.length) {
      var localBFTime = new Date(SNStatus[snIndex].Beamformer.Time);
      var localPSDTime = new Date(SNStatus[snIndex].PSD.Time);
      setUBloxFix(SNStatus[snIndex].UBlox.FixStatus);
      setAvailL1(SNStatus[snIndex].Status.Array[0].Avail);
      setCalL1(SNStatus[snIndex].Status.Array[0].Cal);
      setJammerL1(SNStatus[snIndex].Status.Array[0].Jammer);
      setSpooferL1(SNStatus[snIndex].Status.Array[0].Spoofer);
      setAvailL2(SNStatus[snIndex].Status.Array[1].Avail);
      setCalL2(SNStatus[snIndex].Status.Array[1].Cal);
      setJammerL2(SNStatus[snIndex].Status.Array[1].Jammer);
      setSpooferL2(SNStatus[snIndex].Status.Array[1].Spoofer);
      let BFx: Array<number> = [];
      let BFy: Array<number> = [];
      for (var m = -90; m <= 90; m += 1) {
        BFx.push(m);
        BFy.push(m);
      }
      let BFz: Array<any> = [];
      let Jammerx: Array<number> = [];
      let Jammery: Array<number> = [];
      let Spooferx: Array<number> = [];
      let Spoofery: Array<number> = [];
      let GNSSx: Array<number> = [];
      let GNSSy: Array<number> = [];
      let GNSStext: Array<string> = [];
      let bfl1Data: Array<object> = [];
      BFz = [];
      if (SNStatus[snIndex].Beamformer.Array[0].Beamformer.length > 0) {
        var dMaxPower = -9999.0;
        for (
          var m = 0;
          m < SNStatus[snIndex].Beamformer.Array[0].Beamformer.length;
          ++m
        ) {
          for (
            var n = 0;
            n < SNStatus[snIndex].Beamformer.Array[0].Beamformer[m].length;
            ++n
          ) {
            if (
              dMaxPower < SNStatus[snIndex].Beamformer.Array[0].Beamformer[m][n]
            ) {
              dMaxPower =
                SNStatus[snIndex].Beamformer.Array[0].Beamformer[m][n];
            }
          }
        }
        var dEleStep =
          90.0 / SNStatus[snIndex].Beamformer.Array[0].Beamformer.length;
        let vdAziStep: Array<number> = [];
        for (
          var m = 0;
          m < SNStatus[snIndex].Beamformer.Array[0].Beamformer.length;
          ++m
        ) {
          var dAziStep =
            360.0 / SNStatus[snIndex].Beamformer.Array[0].Beamformer[m].length;
          vdAziStep.push(dAziStep);
        }
        for (var m = -90; m <= 90; ++m) {
          let BFz_row: Array<any> = [];
          for (var n = -90; n <= 90; ++n) {
            var dR2 = m * m + n * n;
            if (dR2 < 8100.0) {
              var dEle = 90.0 - Math.sqrt(m * m + n * n);
              var dAzi = (Math.atan2(n, m) * 180.0) / Math.PI;
              var iEleIndex = Math.round((dEle - dEleStep * 0.5) / dEleStep);
              if (iEleIndex < 0) {
                iEleIndex = 0;
              }
              if (
                iEleIndex >=
                SNStatus[snIndex].Beamformer.Array[0].Beamformer.length
              ) {
                iEleIndex =
                  SNStatus[snIndex].Beamformer.Array[0].Beamformer.length - 1;
              }
              var iAziIndex = Math.round(
                (dAzi - vdAziStep[iEleIndex] * 0.5) / vdAziStep[iEleIndex]
              );
              while (iAziIndex < 0) {
                iAziIndex +=
                  SNStatus[snIndex].Beamformer.Array[0].Beamformer[iEleIndex]
                    .length;
              }
              while (
                iAziIndex >=
                SNStatus[snIndex].Beamformer.Array[0].Beamformer[iEleIndex]
                  .length
              ) {
                iAziIndex -=
                  SNStatus[snIndex].Beamformer.Array[0].Beamformer[iEleIndex]
                    .length;
              }
              BFz_row.push(
                SNStatus[snIndex].Beamformer.Array[0].Beamformer[iEleIndex][
                  iAziIndex
                ]
              );
            } else {
              BFz_row.push(0.0 / 0.0);
            }
          }
          BFz.push(BFz_row);
        }
      } else {
        for (var m = -90; m <= 90; ++m) {
          let BFz_row: Array<any> = [];
          for (var n = -90; n <= 90; ++n) {
            BFz_row.push(0.0 / 0.0);
          }
          BFz.push(BFz_row);
        }
      }
      var data3d = {
        z: BFz,
        x: BFx,
        y: BFy,
        zauto: false,
        zmin: -120,
        zmax: -90,
        zsmooth: "best",
        type: "heatmap",
        colorscale: ColorScale_Normal,
        showscale: false,
        connectgaps: false,
        name: "L1 BF",
      };
      bfl1Data.push(data3d);
      Jammerx = [];
      Jammery = [];
      for (
        var m = 0;
        m < SNStatus[snIndex].Beamformer.Array[0].Jammer.x.length;
        ++m
      ) {
        Jammerx.push(SNStatus[snIndex].Beamformer.Array[0].Jammer.x[m]);
        Jammery.push(SNStatus[snIndex].Beamformer.Array[0].Jammer.y[m]);
      }
      var data = {
        x: Jammerx,
        y: Jammery,
        mode: "lines",
        name: "Jammer",
        line: { color: "rgb(255, 0, 255)" },
      };
      bfl1Data.push(data);
      Spooferx = [];
      Spoofery = [];
      for (
        var m = 0;
        m < SNStatus[snIndex].Beamformer.Array[0].Spoofer.x.length;
        ++m
      ) {
        Spooferx.push(SNStatus[snIndex].Beamformer.Array[0].Spoofer.x[m]);
        Spoofery.push(SNStatus[snIndex].Beamformer.Array[0].Spoofer.y[m]);
      }
      var data = {
        x: Spooferx,
        y: Spoofery,
        mode: "lines",
        name: "Spoofer",
        line: { color: "rgb(255, 255, 0)" },
      };
      bfl1Data.push(data);
      GNSSx = [];
      GNSSy = [];
      GNSStext = [];
      for (var m = 0; m < SNStatus[snIndex].VisGNSS.x.length; ++m) {
        GNSSx.push(SNStatus[snIndex].VisGNSS.x[m]);
        GNSSy.push(SNStatus[snIndex].VisGNSS.y[m]);
        GNSStext.push(SNStatus[snIndex].VisGNSS.text[m]);
      }
      var dataGNSS = {
        x: GNSSx,
        y: GNSSy,
        text: GNSStext,
        mode: "markers+text",
        type: "scatter",
        name: "Sat",
        textfont: { size: 7, color: "white" },
        textposition: "bottom center",
        marker: {
          size: 4,
          symbol: "cross-open",
          color: "yellow",
        },
      };
      bfl1Data.push(dataGNSS);
      setBFL1Data(bfl1Data);
      let bfl1Layout = layout_bfl1;
      bfl1Layout.title.text =
        SNStatus[snIndex].Name +
        " L1 Sky Plot<br>" +
        localBFTime.toLocaleString();
      setBFL1Layout(bfl1Layout);
      let bfl2Data: Array<object> = [];
      BFz = [];
      if (SNStatus[snIndex].Beamformer.Array[1].Beamformer.length > 0) {
        var dMaxPower = -9999.0;
        for (
          var m = 0;
          m < SNStatus[snIndex].Beamformer.Array[1].Beamformer.length;
          ++m
        ) {
          for (
            var n = 0;
            n < SNStatus[snIndex].Beamformer.Array[1].Beamformer[m].length;
            ++n
          ) {
            if (
              dMaxPower < SNStatus[snIndex].Beamformer.Array[1].Beamformer[m][n]
            ) {
              dMaxPower =
                SNStatus[snIndex].Beamformer.Array[1].Beamformer[m][n];
            }
          }
        }
        var dEleStep =
          90.0 / SNStatus[snIndex].Beamformer.Array[1].Beamformer.length;
        let vdAziStep: Array<number> = [];
        for (
          var m = 0;
          m < SNStatus[snIndex].Beamformer.Array[1].Beamformer.length;
          ++m
        ) {
          var dAziStep =
            360.0 / SNStatus[snIndex].Beamformer.Array[1].Beamformer[m].length;
          vdAziStep.push(dAziStep);
        }
        for (var m = -90; m <= 90; ++m) {
          let BFz_row: Array<any> = [];
          for (var n = -90; n <= 90; ++n) {
            var dR2 = m * m + n * n;
            if (dR2 < 8100.0) {
              var dEle = 90.0 - Math.sqrt(m * m + n * n);
              var dAzi = (Math.atan2(n, m) * 180.0) / Math.PI;
              var iEleIndex = Math.round((dEle - dEleStep * 0.5) / dEleStep);
              if (iEleIndex < 0) {
                iEleIndex = 0;
              }
              if (
                iEleIndex >=
                SNStatus[snIndex].Beamformer.Array[1].Beamformer.length
              ) {
                iEleIndex =
                  SNStatus[snIndex].Beamformer.Array[1].Beamformer.length - 1;
              }
              var iAziIndex = Math.round(
                (dAzi - vdAziStep[iEleIndex] * 0.5) / vdAziStep[iEleIndex]
              );
              while (iAziIndex < 0) {
                iAziIndex +=
                  SNStatus[snIndex].Beamformer.Array[1].Beamformer[iEleIndex]
                    .length;
              }
              while (
                iAziIndex >=
                SNStatus[snIndex].Beamformer.Array[1].Beamformer[iEleIndex]
                  .length
              ) {
                iAziIndex -=
                  SNStatus[snIndex].Beamformer.Array[1].Beamformer[iEleIndex]
                    .length;
              }
              BFz_row.push(
                SNStatus[snIndex].Beamformer.Array[1].Beamformer[iEleIndex][
                  iAziIndex
                ]
              );
            } else {
              BFz_row.push(0.0 / 0.0);
            }
          }
          BFz.push(BFz_row);
        }
      } else {
        for (var m = -90; m <= 90; ++m) {
          let BFz_row: Array<any> = [];
          for (var n = -90; n <= 90; ++n) {
            BFz_row.push(0.0 / 0.0);
          }
          BFz.push(BFz_row);
        }
      }
      var data3d = {
        z: BFz,
        x: BFx,
        y: BFy,
        zauto: false,
        zmin: -120,
        zmax: -90,
        zsmooth: "best",
        type: "heatmap",
        colorscale: ColorScale_Normal,
        showscale: false,
        connectgaps: false,
        name: "L2 BF",
      };
      bfl2Data.push(data3d);
      Jammerx = [];
      Jammery = [];
      for (
        var m = 0;
        m < SNStatus[snIndex].Beamformer.Array[1].Jammer.x.length;
        ++m
      ) {
        Jammerx.push(SNStatus[snIndex].Beamformer.Array[1].Jammer.x[m]);
        Jammery.push(SNStatus[snIndex].Beamformer.Array[1].Jammer.y[m]);
      }
      var data = {
        x: Jammerx,
        y: Jammery,
        mode: "lines",
        name: "Jammer",
        line: { color: "rgb(255, 0, 255)" },
      };
      bfl2Data.push(data);
      Spooferx = [];
      Spoofery = [];
      for (
        var m = 0;
        m < SNStatus[snIndex].Beamformer.Array[1].Spoofer.x.length;
        ++m
      ) {
        Spooferx.push(SNStatus[snIndex].Beamformer.Array[1].Spoofer.x[m]);
        Spoofery.push(SNStatus[snIndex].Beamformer.Array[1].Spoofer.y[m]);
      }
      var data = {
        x: Spooferx,
        y: Spoofery,
        mode: "lines",
        name: "Spoofer",
        line: { color: "rgb(255, 255, 0)" },
      };
      bfl2Data.push(data);
      bfl2Data.push(dataGNSS);
      setBFL2Data(bfl2Data);
      let bfl2Layout = layout_bfl2;
      bfl2Layout.title.text =
        SNStatus[snIndex].Name +
        " L2 Sky Plot<br>" +
        localBFTime.toLocaleString();
      setBFL2Layout(bfl2Layout);

      let psdData: Array<object> = [];
      if (SNStatus[snIndex].PSD.Array[0].Power.length > 2) {
        var FreqL1 = converter.getSeriesByLength(
          SNStatus[snIndex].PSD.Array[0].Freq[0],
          SNStatus[snIndex].PSD.Array[0].Freq[
            SNStatus[snIndex].PSD.Array[0].Freq.length - 1
          ],
          SNStatus[snIndex].PSD.Array[0].Power.length
        );
        var data_psd_l1 = {
          x: FreqL1,
          y: SNStatus[snIndex].PSD.Array[0].Power,
          mode: "lines",
          name: "L1",
          line: { color: "rgb(0, 155, 255)" },
        };
        psdData.push(data_psd_l1);
      }
      if (SNStatus[snIndex].PSD.Array[1].Power.length > 2) {
        var FreqL2 = converter.getSeriesByLength(
          SNStatus[snIndex].PSD.Array[1].Freq[0],
          SNStatus[snIndex].PSD.Array[1].Freq[
            SNStatus[snIndex].PSD.Array[1].Freq.length - 1
          ],
          SNStatus[snIndex].PSD.Array[1].Power.length
        );
        var data_psd_l2 = {
          x: FreqL2,
          y: SNStatus[snIndex].PSD.Array[1].Power,
          mode: "lines",
          name: "L2",
          line: { color: "rgb(255, 155, 0)" },
        };
        psdData.push(data_psd_l2);
      }
      setPSDData(psdData);
      let psdLayout = layout_psd;
      psdLayout.title.text =
        SNStatus[snIndex].Name + " PSD " + localPSDTime.toLocaleString();
      setPSDLayout(psdLayout);
    } else {
      setUBloxFix("NOFIX");
      setAvailL1(false);
      setCalL1(false);
      setJammerL1(false);
      setSpooferL1(false);
      setAvailL2(false);
      setCalL2(false);
      setJammerL2(false);
      setSpooferL2(false);
      setBFL1Data(data_bfl1_init);
      setBFL1Layout(layout_bfl1_init);
      setBFL2Data(data_bfl2_init);
      setBFL2Layout(layout_bfl2_init);
      setPSDData(psd_init);
      setPSDLayout(layout_psd_init);
    }
  }, [SNTime, snIndex]);

  return (
    <div className="SNStatus">
        <Table><TableBody>
        <TableRow><Table><TableBody>
                <TableRow style={{backgroundColor: "white", height: "10%"}}>
                    <TableCell style={{width: "100%", backgroundColor: "white", color: "black"}}>
                        Sensor Node Status
                    </TableCell>
                    <TableCell style={{ width: "100%", backgroundColor: "white", color: "black"}}>
                        <Select id="sn-name" value={snIndex} onChange={onSNChange} onOpen={onSNChange} style={{backgroundColor: "rgba(200, 200, 200, 1)", color: "black"}}>
                            {SNList.map((sn, index) => ( <MenuItem value={index}>{sn}</MenuItem>))}
                        </Select>
                    </TableCell>
                </TableRow>
        </TableBody></Table></TableRow>    
        <TableRow><Table><TableBody>
            <TableRow style={{border: "1px solid rgba(255, 255, 255, 1)", backgroundColor: "white", height: "10%"}}>
                <TableCell colSpan={2} style={{ border: "1px solid rgba(255, 255, 255, 1)", width: "100%", backgroundColor: "white"}} padding="none">
                    {(snUBloxFix === "NOFIX" || snUBloxFix === "DRONLY") && (<button className="bnt_red_flash">{"GPS Status - " + snUBloxFix}</button>)}
                    {snUBloxFix === "RESERVED" && (<button className="bnt_orange_flash">{"GPS Status - " + snUBloxFix}</button>)}
                    {(snUBloxFix === "2DFIX" || snUBloxFix === "3DFIX" || snUBloxFix === "GPSANDDR" || snUBloxFix === "TIMEONLY") && (<button className="bnt_green">{"GPS Status - " + snUBloxFix}</button>)}
                </TableCell>
            </TableRow>
            <TableRow style={{border: "1px solid rgba(255, 255, 255, 1)", backgroundColor: "white", height: "10%"}}>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}} padding="none">
                    {snAvailL1 && snJammerL1 && snSpooferL1 && (<button className="bnt_orange_flash">L1 Jammer/Spoofer</button>)}
                    {snAvailL1 && snJammerL1 && !snSpooferL1 && (<button className="bnt_orange_flash">L1 Jammer</button>)}
                    {snAvailL1 && !snJammerL1 && snSpooferL1 && (<button className="bnt_orange_flash">L1 Spoofer</button>)}
                    {snAvailL1 && !snJammerL1 && !snSpooferL1 && (<button className="bnt_green">L1 No Detection</button>)}
                    {!snAvailL1 && (<button className="bnt_grey">L1 No Detection</button>)}
                </TableCell>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}} padding="none">
                    {snAvailL2 && snJammerL2 && snSpooferL2 && (<button className="bnt_orange_flash">L2 Jammer/Spoofer</button>)}
                    {snAvailL2 && snJammerL2 && !snSpooferL2 && (<button className="bnt_orange_flash">L2 Jammer</button>)}
                    {snAvailL2 && !snJammerL2 && snSpooferL2 && (<button className="bnt_orange_flash">L2 Spoofer</button>)}
                    {snAvailL2 && !snJammerL2 && !snSpooferL2 && (<button className="bnt_green">L2 No Detection</button>)}
                    {!snAvailL2 && (<button className="bnt_grey">L2 No Detection</button>)}
                </TableCell>
            </TableRow>
            <TableRow style={{border: "1px solid rgba(255, 255, 255, 1)", backgroundColor: "white", height: "10%"}}>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}} padding="none">
                    {snAvailL1 && snCalL1 && (<button className="bnt_green">L1 Cal</button>)}
                    {snAvailL1 && !snCalL1 && (<button className="bnt_orange_flash">L1 Cal</button>)}
                    {!snAvailL1 && <button className="bnt_grey">L1 Cal</button>}
                </TableCell>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}} padding="none">
                    {snAvailL2 && snCalL2 && (<button className="bnt_green">L2 Cal</button>)}
                    {snAvailL2 && !snCalL2 && (<button className="bnt_orange_flash">L2 Cal</button>)}
                    {!snAvailL2 && <button className="bnt_grey">L2 Cal</button>}
                </TableCell>
            </TableRow>
        </TableBody></Table></TableRow>
        <TableRow><Table><TableBody>    
            <TableRow style={{height: '10%'}} >
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}}>
                    <Plot data={data_bfl1 as any} layout={layout_bfl1} useResizeHandler={true} style={{ width: '100%', height: '100%' }}/>
                </TableCell>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "50%", backgroundColor: "white"}}>
                    <Plot data={data_bfl2 as any} layout={layout_bfl2} useResizeHandler={true} style={{ width: '100%', height: '100%' }}/>
                </TableCell>
            </TableRow>
        </TableBody></Table></TableRow>
        <TableRow><Table><TableBody>  
            <TableRow>
                <TableCell style={{border: "1px solid rgba(255, 255, 255, 1)", width: "100%", backgroundColor: "white"}}>
                    <Plot data={data_psd as any} layout={layout_psd} useResizeHandler={true} style={{ width: '100%', height: '100%' }}/>
                </TableCell>
            </TableRow>
        </TableBody></Table></TableRow>
        </TableBody></Table>
    </div>
  );
}
